import { useEffect, useState } from 'react'
import { ReactFC } from 'common/types/ReactFC'

import useWindowSize from 'components/util/useWindowSize'
import runWhenIdle from 'common/runWhenIdle'
import { useNearViewport } from 'common/useNearViewport'

type EmbededKonnectFormType = {
  className?:string
}

const formId = 'newsletter-form'
const EmbededKonnectForm: ReactFC<EmbededKonnectFormType> = ({children,
                                                             className='col-lg-6 mt-8 px-lg-2 px-4'
                                                             }) => {
  const size = useWindowSize()
  const customStyle = `
.hs-form-private {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.legal-consent-container {
  display: none !important;
}
label {
  display: none !important;
}
.hs-form-field {
  width: 100% !important;
  margin-bottom: 0 !important;
}
.hs-input:not([type=file]) {
  height: 52px;
  border-radius: 38px;
  background-color: #ededef;
  padding: 10px 20px;
}
.hs-input:not([type=file]):focus {
  background-color: white;
}
.actions {
  padding: 0 !important;
  margin: 0 !important;
}
.hs-error-msgs label {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #cb0909 !important;
}
.hs-button {
  background-color: #0071e3 !important;
  border-radius: 38px !important;
  height: 52px !important;
  padding: 10px 20px !important;
  border: none !important;
  font-weight: 300;
  font-size: 16px !important;
  font-family: "Open Sans", sans-serif !important;
  width: 180px !important;
}
.input.hs-input.error {
  border-color: #cb0909 !important;
}
.submitted-message p {
  font-size: 24px !important;
  font-weight: 700 !important;
}
.submitted-message p span {
  font-weight: 400 !important;
  font-size: 16px !important;
}

@media (max-width: 500px) {
  .hs-form-private {
    flex-direction: column;
  }
  .actions,.hs-button {
    width: 100% !important;
  }
}
`

  const [submitted, setSubmitted] = useState(false)

  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  useEffect(() => {
    if (localStorage.getItem(formId)) {
      return setSubmitted(true)
    }
  }, [])

  const [ref] = useNearViewport({
    onEnter: () => {
      if (window.hbspt?.forms) {
        setIsScriptLoaded(true)
        return
      }

      const src = '//js.hsforms.net/forms/embed/v2.js'
      let script = document.querySelector<HTMLScriptElement>(
        `script[src="${src}"]`,
      )

      if (script) {
        if (!window.hbspt?.forms) {
          script.addEventListener('load', () => setIsScriptLoaded(true), {
            once: true,
          })
        }
        return
      }

      script = document.createElement('script')
      script.src = src
      script.async = true
      script.onload = () => {
        setIsScriptLoaded(true)
      }
      document.head.appendChild(script)
    },
  })

  useEffect(() => {
    if (!isScriptLoaded || !window.hbspt?.forms) return

    if (window.hbspt?.forms) {
      window.hbspt.forms.create({
        formId: 'fcdf4d36-a2ee-40be-b817-16245eedfbd3',
        onFormReady: function () {
          const formIframe = document.querySelector(
            '#newsletter-form-container iframe',
          )
          if (formIframe) {
            const iframeDoc =
              formIframe.contentDocument || formIframe.contentWindow.document
            const style = iframeDoc.createElement('style')
            style.textContent = customStyle
            iframeDoc.head.appendChild(style)
          }
        },
        onFormSubmit: function (form, formDataArray) {
          // Create a result object
          const result: Record<string, any> = {}
          const ignoredKeys = [
            'hs_context',
            '0-2/name',
            'LEGAL_CONSENT.subscription_type_458830746',
          ]

          // Iterate through the array
          formDataArray.forEach(({ name, value }) => {
            if (!ignoredKeys.includes(name)) {
              result[name] = value
            }
          })
          localStorage.setItem(formId, 'true')
        },
        portalId: '48114419',
        target: '#newsletter-form-container',
      })
    }
  }, [isScriptLoaded])

  if (submitted) {
    return null
  }
  return (
    <div ref={ref} id={'newsletter-form'}>
      {children?<>
        {children}
        <div
          id={'newsletter-form-container'}
        />
      </>:(
        <div className='d-flex flex-column align-items-center'>
          <div className='d-flex align-items-center gap-2 mb-2'>
            <svg
              width='26'
              height='26'
              viewBox='0 0 26 26'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M18.2 23.4V2.6H2.6V22.1C2.6 22.4448 2.73696 22.7754 2.98076 23.0192C3.22456 23.263 3.55522 23.4 3.9 23.4H18.2ZM22.1 26H3.9C2.86566 26 1.87368 25.5891 1.14228 24.8577C0.410892 24.1263 0 23.1343 0 22.1V1.3C0 0.955218 0.136964 0.624558 0.380761 0.380761C0.624558 0.136964 0.955218 0 1.3 0H19.5C19.8448 0 20.1754 0.136964 20.4192 0.380761C20.663 0.624558 20.8 0.955218 20.8 1.3V10.4H26V22.1C26 23.1343 25.5891 24.1263 24.8577 24.8577C24.1263 25.5891 23.1343 26 22.1 26ZM20.8 13V22.1C20.8 22.4448 20.937 22.7754 21.1808 23.0192C21.4246 23.263 21.7552 23.4 22.1 23.4C22.4448 23.4 22.7754 23.263 23.0192 23.0192C23.263 22.7754 23.4 22.4448 23.4 22.1V13H20.8ZM5.2 5.2H13V13H5.2V5.2ZM7.8 7.8V10.4H10.4V7.8H7.8ZM5.2 14.3H15.6V16.9H5.2V14.3ZM5.2 18.2H15.6V20.8H5.2V18.2Z'
                fill='#1D1D1F'
              />
            </svg>
            <h2 className='fs-fluid-20-34 text-gradient-purple mb-0'>
              Sign up to our newsletter
            </h2>
          </div>
          <span className='fs-fluid-20-24 fw-semibold text-lg-start text-center'>
            Sign up to our newsletter to receive the latest updates
          </span>

          <div
            id={'newsletter-form-container'}
            className={className}
          />
        </div>
      )}


    </div>
  )
}

export default EmbededKonnectForm
